<template>
  <b-row>
    <b-col md="2" sm="4" class="my-1">
      <b-form-group class="mb-0">
        <label class="d-inline-block text-sm-left mr-50">Per page</label>
        <b-form-select id="perPageSelect" v-model="perPage" size="sm" :options="pageOptions" class="w-50" />
      </b-form-group>
    </b-col>

    <b-col cols="12" class="d-flex justify-content-end mb-2" style="position: relative; z-index: 1;">
      <b-button variant="outline-danger" size="sm" @click="clearFilters">
        Clear Filters
      </b-button>
    </b-col>


    <b-col cols="12">
      <b-table striped hover responsive :per-page="perPage" :current-page="currentPage" :items="filteredInvoices"
        :fields="fields" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :sort-direction="sortDirection"
        @filtered="onFiltered">

        <!-- Invoice No Filter -->
        <template #head(invoiceNo)="scope">
          <div class="d-flex flex-column">
            <span>Invoice No</span>
            <b-form-input v-model="filters.invoiceNo" placeholder="Filter by Invoice No" size="sm"
              @input="applyFilters" />
          </div>
        </template>

        <!-- Created On Filter -->
        <template #head(createdOn)="scope">
          <div class="d-flex flex-column">
            <span @click="toggleSort('createdOn')" style="cursor: pointer">Created On</span>
            <DatePicker v-model="filters.createdOnRange" range format="YYYY-MM-DD" placeholder="Select date range"
              @change="applyFilters" style="width: 100%;" />
          </div>
        </template>

        <!-- Due Date Filter -->
        <template #head(dueDate)="scope">
          <div class="d-flex flex-column">
            <span @click="toggleSort('dueDate')" style="cursor: pointer">Due Date</span>
            <DatePicker v-model="filters.dueDateRange" range format="YYYY-MM-DD" placeholder="Select date range"
              @change="applyFilters" style="width: 100%;" />
          </div>
        </template>

        <template v-slot:cell(dueDate)="data">
          {{ convertTimestampToDate(data.item.dueDate) }}
        </template>

        <template v-slot:cell(createdOn)="data">
          {{ convertTimestampToDate(data.item.createdOn) }}
        </template>

        <template v-slot:cell(total)="data">
          Rs {{ data.item.total.toFixed(2) }}
        </template>


        <template v-slot:cell(actions)="data">
          <!-- redirect to ViewInvoice.vue -->
          <b-button variant="primary" v-if="HasPermission(PermissionList.Sales_View)" :to="getInvoiceId(data.item.id)">
            <feather-icon icon="EyeIcon" class="mr-50" /> View
          </b-button>
        </template>
      </b-table>
      <div class="d-flex justify-content-center mb-1">
        <b-alert variant="warning" show v-if="invoiceList.length === 0" class="d-flex justify-content-center">
          <h4 class="alert-heading">No Invoices yet</h4>
        </b-alert>
      </div>
      <div class="d-flex justify-content-center mb-1">
        <b-spinner variant="success" label="Spinning" block v-if="loading"
          style="width: 3rem; height: 3rem"></b-spinner>
      </div>
    </b-col>

    <b-col cols="12">
      <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" align="center" size="sm"
        class="my-0" />
    </b-col>
  </b-row>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css"
import {
  BTable,
  BAvatar,
  BBadge,
  BRow,
  BAlert,
  BCol,
  BFormGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BDropdown,
  BDropdownItem,
  BForm,
  BFormCheckbox,
  BSpinner,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "@validations";
import { mapActions, mapGetters } from "vuex";
import { PermissionList } from '@/libs/defaultList';
import { HasPermission } from '@/@core/utils/validations/permissionCheck';

export default {
  components: {
    BTable,
    BAvatar,
    BBadge,
    BAlert,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormCheckbox,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BDropdown,
    BDropdownItem,
    ValidationProvider,
    ValidationObserver,
    BSpinner,
    DatePicker,
    ///
    required,
  },
  data() {
    return {
      isActive: false,
      id: this.$route.params.id,
      labourName: "",
      quantity: "",
      costPerHour: "",
      perPage: 5,
      invoiceData: [],
      pageOptions: [3, 5, 10],
      totalRows: 1,
      currentPage: 1,
      sortBy: "",
      sortDesc: false,
      sortDirection: "asc",
      filter: null,
      filterOn: [],
      infoModal: {
        id: "info-modal",
        title: "",
        content: "",
      },
      filters: {
        invoiceNo: '',
        createdOnRange: [null, null],
        dueDateRange: [null, null],
      },
      fields: [
        { key: "invoiceNo", label: "Invoice No", sortable: true },
        { key: "total", label: "Total", sortable: true },
        { key: "createdOn", label: "Created On", sortable: true },
        { key: "dueDate", label: "Due Date", sortable: true },
        { key: "actions", label: "Actions" } // Actions column typically isn't sortable
      ]

    };
  },
  computed: {
    filteredInvoices() {
      return this.invoiceList.filter((item) => {
        let invoiceNoMatch = true;
        let createdOnMatch = true;
        let dueDateMatch = true;

        if (this.filters.invoiceNo) {
          invoiceNoMatch = item.invoiceNo.toString().toLowerCase().includes(this.filters.invoiceNo.toLowerCase());
        }

        if (this.filters.createdOnRange && this.filters.createdOnRange[0] && this.filters.createdOnRange[1]) {
          const [start, end] = this.filters.createdOnRange.map((date) => new Date(date).getTime());
          const createdOn = new Date(item.createdOn).getTime();
          createdOnMatch = createdOn >= start && createdOn <= end;
        }

        if (this.filters.dueDateRange && this.filters.dueDateRange[0] && this.filters.dueDateRange[1]) {
          const [start, end] = this.filters.dueDateRange.map((date) => new Date(date).getTime());
          const dueDate = new Date(item.dueDate).getTime();
          dueDateMatch = dueDate >= start && dueDate <= end;
        }

        return invoiceNoMatch && createdOnMatch && dueDateMatch;
      });
    },
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter((f) => f.sortable)
        .map((f) => ({ text: f.label, value: f.key }));
    },
    ...mapGetters("customerModule", {
      invoiceList: "invoice",
      loading: "loading",
    }),
  },
  mounted() {
    // Set the initial number of items
    this.totalRows = this.invoiceList.length;
  },
  methods: {
    applyFilters() {
      this.currentPage = 1; // Reset pagination to the first page
    },
    clearFilters() {
      // Reset all filters to their initial values
      this.filters = {
        invoiceNo: '',
        createdOnRange: [null, null],
        dueDateRange: [null, null],
        // Add other filter fields as needed
      };
      this.filter = ''; // Clear the text input filter if applicable
      this.currentPage = 1; // Optionally reset pagination to the first page
    },
    convertTimestampToDate(timestamp) {
      return new Date(timestamp).toLocaleDateString();
    },
    getInvoiceData(data) {
      this.labourData = data;
    },
    ...mapActions("customerModule", ["getInvoicesByIDAction"]),
    info(item, index, button) {
      this.infoModal.title = `Row index: ${index}`;
      this.infoModal.content = JSON.stringify(item, null, 2);
      this.$root.$emit("bv::show::modal", this.infoModal.id, button);
    },

    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    getInvoiceId(item) {
      return "/invoice/view/" + item;
    },
  },
  async mounted() {
    await this.getInvoicesByIDAction(this.id)
      .then(() => {
        console.log(this.invoiceList)
        this.totalRows = this.invoiceList.length;
      })
      .catch((response) => {
        console.log(response);
      });
  },
  setup(props) {
    return {
      PermissionList,
      HasPermission
    }
  }

};
</script>
